<template>
  <section class="fis">
    <div class="fis__head">
      <span class="fis__label">Datos de facturación</span>
      <button v-if="!editing && !readOnly" class="fis__link" @click="startEdit">{{ hasData ? 'Editar' : 'Agregar' }}</button>
    </div>

    <template v-if="!editing">
      <p v-if="!hasData" class="fis__empty">Agrega tus datos fiscales para que Parkos pueda emitirte tus facturas.</p>
      <dl v-else class="fis__grid">
        <div><dt>Razón social</dt><dd>{{ fiscal.legalName || '—' }}</dd></div>
        <div><dt>RFC</dt><dd class="mono">{{ fiscal.rfc || '—' }}</dd></div>
        <div><dt>Régimen fiscal</dt><dd>{{ fiscal.taxRegime || '—' }}</dd></div>
        <div><dt>Código postal</dt><dd class="mono">{{ fiscal.zip || '—' }}</dd></div>
        <div><dt>Correo para facturas</dt><dd>{{ fiscal.email || '—' }}</dd></div>
      </dl>
    </template>

    <form v-else class="fis__form" @submit.prevent="save">
      <label class="f f--wide"><span>Razón social</span><input v-model="form.legalName" class="fi" maxlength="150" /></label>
      <label class="f"><span>RFC</span><input v-model="form.rfc" class="fi mono" maxlength="13" placeholder="ABC123456XX1" :class="{ 'fi--bad': form.rfc && !isValidRfc(form.rfc) }" @input="form.rfc = form.rfc.toUpperCase()" /></label>
      <label class="f"><span>Código postal</span><input v-model="form.zip" class="fi mono" inputmode="numeric" maxlength="5" :class="{ 'fi--bad': form.zip && !/^\d{5}$/.test(form.zip) }" @input="form.zip = form.zip.replace(/\D/g, '')" /></label>
      <label class="f f--wide"><span>Régimen fiscal</span>
        <select v-model="form.taxRegime" class="fi">
          <option value="">Selecciona…</option>
          <option v-for="r in regimes" :key="r" :value="r">{{ r }}</option>
        </select>
      </label>
      <label class="f f--wide"><span>Correo para facturas</span><input v-model="form.email" type="email" class="fi" :class="{ 'fi--bad': form.email && !isValidEmail(form.email) }" /></label>
      <p v-if="error" class="fis__error">{{ error }}</p>
      <div class="fis__actions">
        <button type="button" class="btn btn--ghost" :disabled="saving" @click="editing = false">Cancelar</button>
        <button type="submit" class="btn" :disabled="saving || !valid">{{ saving ? 'Guardando…' : 'Guardar' }}</button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { saveFiscalData } from '@/services/planBilling'
import { TAX_REGIMES, isValidRfc, isValidEmail, errorText } from './planFormat'

const props = defineProps({ parkId: { type: String, required: true }, fiscal: { type: Object, required: true }, readOnly: Boolean })
const emit = defineEmits(['saved'])

const editing = ref(false)
const saving = ref(false)
const error = ref('')
const form = reactive({ legalName: '', rfc: '', taxRegime: '', zip: '', email: '' })

const hasData = computed(() => Object.values(props.fiscal).some(Boolean))
// Si el régimen guardado no está en la lista (dato viejo), igual se muestra como opción.
const regimes = computed(() => (props.fiscal.taxRegime && !TAX_REGIMES.includes(props.fiscal.taxRegime) ? [props.fiscal.taxRegime, ...TAX_REGIMES] : TAX_REGIMES))
const valid = computed(() => (!form.rfc || isValidRfc(form.rfc)) && (!form.zip || /^\d{5}$/.test(form.zip)) && (!form.email || isValidEmail(form.email)))

function startEdit() {
  Object.assign(form, {
    legalName: props.fiscal.legalName || '', rfc: props.fiscal.rfc || '', taxRegime: props.fiscal.taxRegime || '',
    zip: props.fiscal.zip || '', email: props.fiscal.email || '',
  })
  error.value = ''
  editing.value = true
}

async function save() {
  if (!valid.value) return
  saving.value = true
  error.value = ''
  try {
    const fiscal = await saveFiscalData(props.parkId, { ...form })
    emit('saved', fiscal)
    editing.value = false
  } catch (err) {
    error.value = errorText(err, 'No se pudieron guardar los datos. Intenta de nuevo.')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.fis { padding: 16px 18px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; box-sizing: border-box; }
.fis__head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.fis__label { font: 500 11px var(--font-body); color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; }
.fis__link { background: none; border: none; padding: 0; cursor: pointer; font: 400 12px var(--font-body); color: #3b82f6; }
.fis__empty { margin: 0; font: 400 12px var(--font-body); color: #94a3b8; }
.fis__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 14px 20px; margin: 0; }
.fis__grid dt { font: 400 11px var(--font-body); color: #94a3b8; margin-bottom: 2px; }
.fis__grid dd { margin: 0; font: 400 13px var(--font-body); color: #1e3a5f; overflow-wrap: anywhere; }
.mono { font-family: var(--font-mono) !important; }
.fis__form { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.f { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.f--wide { grid-column: 1 / -1; }
.f span { font: 500 12px var(--font-body); color: #475569; }
.fi { box-sizing: border-box; width: 100%; height: 38px; padding: 0 10px; border: 1px solid #e2e8f0; border-radius: 8px; font: 400 13px var(--font-body); color: #1f2937; background: #fff; }
.fi:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15); }
.fi--bad { border-color: #ef4444; }
.fis__error { grid-column: 1 / -1; margin: 0; font: 400 12px var(--font-body); color: #b91c1c; }
.fis__actions { grid-column: 1 / -1; display: flex; justify-content: flex-end; gap: 8px; }
.btn { height: 36px; padding: 0 16px; border: none; border-radius: 8px; background: #3b82f6; color: #fff; font: 500 13px var(--font-body); cursor: pointer; }
.btn:disabled { opacity: 0.55; cursor: default; }
.btn--ghost { background: none; color: #64748b; }
@media (max-width: 640px) { .fis__form { grid-template-columns: 1fr; } }
</style>